import { Card, Typography, Input, Button, Form, DatePicker, Table } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import "jspdf-autotable";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useCallback, useEffect, useState } from "react";
import { changeLoader } from "../../../../../../../redux/reducers/loader";
import toast from "react-hot-toast";
import globalRequest from "../../../../../../../utils/global/globalRequest";
import { debounce } from "lodash";
import * as Apis from "../../../../../../../utils/helper/enum";
import { useDispatch } from "react-redux";
import { paginationLimit } from "../../../../../../../utils/helper/constants";
import dayjs from "dayjs";
import * as Validation from "../../../../../../../utils/helper/validation";
import { exportCSV, exportPDf, getTabFromOrderStatus, roundToDecimal } from "../../../../../../../utils/helper/utils";

const expandColumns = [
  {
    title: "Order Number",
    dataIndex: "OrderNumber",
    key: "OrderNumber",
    render: (text, record) => (
      <Link className="cursor-pointer" to={`/order-management/?order_number=${record?.OrderNumber}`}
        state={{
          orderNumber: record?.OrderNumber,
          activeTab: getTabFromOrderStatus(record?.Status)
        }}
        style={{ color: "#37846B", fontWeight: 500 }}
      >
        {text}
      </Link>
    ),
  },
  {
    title: "Branch Name",
    dataIndex: "BranchName",
    key: "BranchName",
  },
  {
    title: "Driver Name",
    dataIndex: "DriverName",
    key: "DriverName",
  },
  {
    title: "Delivery Mode",
    dataIndex: "DeliveryMode",
    key: "DeliveryMode",
  },
  {
    title: "Dropzone",
    dataIndex: "Dropzone",
    key: "Dropzone",
  },
  {
    title: "Zone",
    dataIndex: "Zone",
    key: "Zone",
  },
  {
    title: "Payment Method",
    dataIndex: "PaymentMethod",
    key: "PaymentMethod",
    render: (method) => {
        if (method === "credit_card") return "Credit Card";
        if (method === "apple_pay") return "Apple Pay";
        return method || "-";
    },
  },
  {
    title: "Status",
    dataIndex: "Status",
    key: "Status",
  },
  {
    title: "Total Amount",
    dataIndex: "TotalAmount",
    key: "TotalAmount",
  },
  {
    title: "Order Date",
    dataIndex: "OrderDate",
    key: "OrderDate",
  },
  {
    title: "Delivery Date",
    dataIndex: "DeliveryDate",
    key: "DeliveryDate",
  },
];

const VendorDataReport = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { vendorId  } = useParams();

  const [form] = Form.useForm();
  const { RangePicker } = DatePicker;
  const { Search } = Input;
  const [dataSource, setDataSource] = useState([]);
  const [paginationModel] = useState(paginationLimit);
  const { search, start_date, end_date } = location.state?.inputData || {};
  const tabIndex = location.state?.tabIndex;
  const [inputData, setInputData] = useState({
    search: search,
    start_date: start_date,
    end_date: end_date, 
  });

  const handleDateChange = (dates, dateStrings) => {
    const [startdate, enddate] = dateStrings; 

    setInputData((prevState) => ({
      ...prevState,
      start_date: startdate, 
      end_date: enddate, 
    }));
  };
  const handleSearchDebounced = debounce((value) => {
    setInputData((prev) => ({ ...prev, search: value }));
  }, 300); 

  const fetchOrdersForVendorDetails = useCallback(async (record) => {
        dispatch(changeLoader(true));
        try {
        const response = await globalRequest(
            `${Apis.MANAGE_REPORT.GET_ALL_PAYMENTS_REPORT_BY_VENDOR_ORDERS}/${vendorId}`,
            "get",
            {},
            { params: { ...inputData, page: paginationModel.page, limit: paginationModel.limit } },
            true
        );

        if (response?.data?.status === "SUCCESS") {
            const orders =
            response?.data?.data?.data?.data?.map((order, index) => ({
                key: index,
                OrderNumber: order.orderNumber,
                BranchName: order.branchName,
                DriverName: order.driverName,
                DeliveryMode: order.deliveryMode,
                Dropzone: order.dropZone,
                Zone: order.zone,
                PaymentMethod: order.paymentMethod,
                Status: order.status,
                TotalAmount: roundToDecimal(order.totalAmount, 2),
                OrderDate: order.orderDate,
                DeliveryDate: order.deliveryDate,
            })) || [];

            setDataSource(orders);
        }
        } catch (e) {
        toast.error(e?.message || "Something went wrong while fetching vendor orders");
        }
        dispatch(changeLoader(false));
    }, [dispatch, vendorId, inputData, paginationModel.page, paginationModel.limit]
  );

  const exportData = async (value) => {
    if (value) {
      dispatch(changeLoader(true));
      try {
        const response = await globalRequest(
        `${Apis.MANAGE_REPORT.GET_ALL_PAYMENTS_REPORT_BY_VENDOR_ORDERS}/${vendorId}`,
        "get",
        {},
        { params: { ...inputData, page: paginationModel.page, limit: paginationModel.limit } },
        true
        );
        let apiResponse = response.data;
        if (apiResponse?.status === "SUCCESS") {
          if (apiResponse?.data) {
            let exportData = [];
            for (let key in apiResponse?.data?.data?.data) {
              let item = apiResponse?.data?.data?.data[key];
              exportData.push({
                OrderNumber: item.orderNumber,
                BranchName: item.branchName,
                DriverName: item.driverName,
                DeliveryMode: item.deliveryMode,
                Dropzone: item.dropZone,
                Zone: item.zone,
                PaymentMethod: item.paymentMethod,
                Status: item.status,
                TotalAmount: roundToDecimal(item.totalAmount, 2),
                OrderDate: item.orderDate,
                DeliveryDate: item.deliveryDate,
              });
            }
            if (exportData.length > 0) {
              if (value === "csv") {
                exportCSV(exportData, "vendorDataReport.csv");
              }
              if (value === "pdf") {
                const headers = ["order Number", "Branch Name", "Driver Name", "Delivery Mode", "Dropzone", "Zone", "Payment Method", "Status", "Total Amount", "Order Date", "Delivery Date"];
                const rows = exportData.map((item) => [item.OrderNumber, item.BranchName, item.DriverName, item.DeliveryMode, item.Dropzone, item.Zone, item.PaymentMethod, item.Status, item.TotalAmount, item.OrderDate, item.DeliveryDate]);
                exportPDf(headers, rows, "vendorDataReport.pdf");
              }
            }
          }
        } else {
          toast.error(apiResponse?.message);
        }
      } catch (e) {
        toast.error(e?.message);
      }
      dispatch(changeLoader(false));
    }
  };

  useEffect(() => {
    if (vendorId) {
    fetchOrdersForVendorDetails();
    }
  }, [inputData, paginationModel, vendorId, fetchOrdersForVendorDetails]);

  return (
    <>
      <Card>
        <div className="flex flex-wrap justify-between	mb-4 gap-5 ">
          <div className="">
            <Typography className="text-xl font-semibold flex gap-1" align={"left"}>
              <Link to="/report" className="!text-[#176B53]" state={{ 
                  inputData: inputData,
                  mainTabIndex: "1", 
                  subTabIndex: tabIndex 
                }}>
                Reports /{" "}
              </Link>
              <Typography
                onClick={() => navigate("/report", { state: { inputData: inputData,
                    mainTabIndex: "1",
                    subTabIndex: tabIndex 
                  } 
                })}
                className="!text-xl !text-[#176B53] cursor-pointer"
              >
                Sales Reports / Sales By Vendor
              </Typography>
            </Typography>
          </div>
          <div className="flex flex-wrap gap-3">
            <Form form={form} layout="vertical">
              <Form.Item name="search">
                <div className="flex flex-wrap gap-3">
                  <RangePicker
                    className="cursor-pointer"
                    onChange={handleDateChange}
                    value={[
                      inputData?.start_date ? dayjs(inputData?.start_date, "YYYY-MM-DD") : null,
                      inputData?.end_date ? dayjs(inputData?.end_date, "YYYY-MM-DD") : null,
                    ]}
                    format="YYYY-MM-DD"
                    allowClear={false}
                  />
                  <Search
                    placeholder="Search"
                    style={{
                      width: window.innerWidth < 768 ? "100%" : 200,
                    }}
                    className="cursor-pointer"
                    onChange={(e) => {
                      const value = Validation.strFormat(e.target.value, "trim");
                      handleSearchDebounced(value);
                    }}
                  />
                </div>
              </Form.Item>
            </Form>
            <div className="flex flex-wrap gap-3">
              <>
                <Button
                  type="primary"
                  size={14}
                  icon={<DownloadOutlined />}
                  className="cursor-pointer"
                  onClick={() => {
                    exportData("csv");
                  }}
                >
                  Download in CSV
                </Button>
                <Button
                  type="primary"
                  size={14}
                  icon={<DownloadOutlined />}
                  className="cursor-pointer"
                  onClick={() => {
                    exportData("pdf");
                  }}
                >
                  Download in PDF
                </Button>
              </>
            </div>
          </div>
        </div>
        <Table columns={expandColumns} dataSource={dataSource} pagination={true} className="multi-tables responsive" />
      </Card>
    </>
  );
};
export default VendorDataReport;
